import {useNavigate, useParams} from "react-router";
import {useEffect, useState} from "react";
import {Separator} from "@/components/ui/separator.tsx";
import {PencilIcon, PlusIcon, SendIcon, X} from "lucide-react";
import {Button} from "@/components/ui/button.tsx";
import {Card, CardContent} from "@/components/ui/card.tsx";
import {Checkbox} from "@/components/ui/checkbox.tsx";
import CurrencyField from "@/components/editor/fields/CurrencyField.tsx";
import axios from "axios";
import {format} from "date-fns";
import {EmailValidator, hasError, RequiredValidator, useForm} from "@/components/form/Helpers.ts";
import Form from "@/components/form/Form.tsx";
import TextField from "@/components/editor/fields/TextField.tsx";
import FormActions from "@/components/form/FormActions.tsx";
import FieldArray from "@/components/form/FieldArray.tsx";
import {useAuth} from "react-oidc-context";
import {cn} from "@/lib/utils.ts";
import {toast} from "sonner";
import {ClipboardCopyIcon, ReloadIcon} from "@radix-ui/react-icons";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip.tsx";

function Overview() {
    const navigate = useNavigate()
    const params = useParams();
    const [document, setDocument] = useState<any>(null);
    const auth = useAuth()

    const [confirmForm, setConfirmForm] = useForm()
    const [recipientsForm, setRecipientsForm] = useForm()

    const [confirmInitialValues, setConfirmInitialValues] = useState({})

    useEffect(() => {
        (async function () {
            loadDocument()
        })();
    }, [params])

    const loadDocument = () => {
        axios.get(`/api/documents/${params.id}?include=order`).then(res => {
            setDocument(res.data)
            setConfirmInitialValues(res.data?.order?.confirm ?? {
                ...res.data?.form?.contact,
                price: res.data?.form?.tanulmany_dij
            })
        })
    }

    const companyMonogram = document?.company?.name?.split(' ').slice(0, -1).splice(0, 2).map((part: string) => part.substring(0, 1)).join('')
    const documentModules = [
        {
            id: 'lojalitas',
            name: 'Lojalitás'
        },
        {
            id: 'torzsgarda',
            name: 'Törzsgárda'
        },
        {
            id: 'szindikatus',
            name: 'Szindikátus'
        },
        {
            id: 'tokekepzes',
            name: 'Tőkeképzés'
        },
        {
            id: 'nyugdij',
            name: 'Nyugdíj'
        },
        {
            id: 'generacio',
            name: 'Generáció váltás'
        }
    ]

    const [confirmLoading, setConfirmLoading] = useState(false)
    const [recipientsLoading, setRecipientsLoading] = useState(false)

    const submitConfirm = async () => {
        setConfirmLoading(true)
        axios.post(`/api/orders`, {
            ...confirmForm.fieldValues,
            document_id: document.id
        }).then(() => {
            loadDocument()
        }).finally(() => setConfirmLoading(false))

    }
    const submitAccess = async () => {
        setRecipientsLoading(true)
        axios.put(`/api/orders/${document.order.id}`, recipientsForm.fieldValues)
            .then(() => {
                loadDocument()
            })
            .finally(() => {
                setRecipientsLoading(false);
                setRecipientsForm("fieldValues", {})
            })
    }

    const updateDocStatus= ( status: string) => {
        axios.post(`/api/documents/${document.id}`, {form: document.form, status: status}).then(() => {
            toast("Változtatás elmentve!")
        })
    }


    const handleOrderReset = () => {

        toast.promise(
            async () => {
                await axios.delete(`/api/documents/${document.id}/order`)
                loadDocument()
            },
            {
                loading: "Művelet folyamatban...",
                success: "Sikeres visszaállítás",
                error: "Hiba történt.",
            }
        )
    }

    const setClipboard = async (text: string)=> {
        const type = "text/plain";
        const clipboardItemData = {
            [type]: text,
        };
        const clipboardItem = new ClipboardItem(clipboardItemData);
        await navigator.clipboard.write([clipboardItem]);
    }


    return (<>
            {document == null ? <></> : <div className={"flex flex-col h-full"}>
                <div className="container mx-auto p-4">
                    <div className={"flex justify-between"}>
                        <div className={"flex gap-3"}>
                            <div
                                className={"w-36 h-36 bg-primary rounded-md text-white font-black text-5xl flex items-center justify-center"}>
                                <div className={"text-center my-auto"}>{companyMonogram}</div>

                            </div>
                            <div className={"flex flex-col justify-between"}>
                            <span
                                className={"font-black text-xl"}>{document.form.title ?? 'Vállalati Stratégia Tanulmány'}</span>
                                <span className={"font-black text-xl"}>{document.company?.name}</span>
                                <span className={"text-muted-foreground"}>Adószám: {document.company?.tax_number}</span>
                                <span
                                    className={"text-muted-foreground"}>Cégjegyzékszám: {document.company?.registration_number}</span>
                                <span
                                    className={"text-muted-foreground font-bold"}>Rendszerbe bekerült: {format(document.company?.created_at, "yyyy.MM.dd.")}</span>
                            </div>
                        </div>

                        <div className={"flex flex-col gap-3"}>
                            <Button className={"w-full min-w-36 font-bold"} onClick={() => updateDocStatus("ARCHIVED_SUCCESSFUL")}>Sikeres volt!</Button>
                            <Button variant={'secondary'} className={"w-full"} onClick={() => updateDocStatus("ARCHIVED_CANCELLED")}>Elutasítva</Button>
                        </div>

                    </div>

                    <div className={"flex flex-col mt-5"}>
                        <span className={"text-muted-foreground font-black"}>Teendők:</span>
                        <span className={cn(
                            "text-muted-foreground font-semibold inline-flex items-center gap-2",
                            {"text-constructive": document.order}
                        )}>
                            <Checkbox
                                aria-readonly
                                checked={document.order != null}/> {document.order == null ? "Megrendelés visszaigazolás kiküldése" : `Megrendelés visszaigazolás kiküldve (${format(document.order.created_at, 'yyyy.MM.dd.')})`}</span>

                        <span
                            className={cn("text-muted-foreground font-semibold inline-flex items-center gap-2", {"text-constructive": document.order?.confirmed})}>
                            <Checkbox
                                aria-readonly
                                checked={document.order?.confirmed}/> {document.order?.confirmed ? `Megrendelés megerősítve (${format(document.order?.confirmed_at, 'yyyy.MM.dd.')})` : "Megrendelés megerősítésre vár"}</span>

                        <span
                            className={cn("text-muted-foreground font-semibold inline-flex items-center gap-2", {"text-constructive": document.order?.recipients})}>
                            <Checkbox
                                aria-readonly
                                checked={document.order?.recipients != null}/> {document.order?.recipients ? `Elkészült dokumentum kiküldve (${format(document.order.updated_at, 'yyyy.MM.dd.')})` : "Az elkészült dokumentum küldésre vár"}</span>
                    </div>
                    <div className={"flex mt-3 gap-2"}>
                        <Button asChild variant={'outline'} className={"min-w-32"}><a
                            href={`/api/company/${document.company.id}/abstract?api_key=${auth?.user?.access_token}`}
                            target={'_blank'}>Kivonat</a></Button>
                        <Button asChild variant={'outline'} className={"min-w-32"}><a
                            href={`/api/print/${document.id}?api_key=${auth?.user?.access_token}`} target={'_blank'}>Irodai
                            verzió</a></Button>
                        <Button asChild variant={'outline'} className={"min-w-32"}><a
                            href={`/api/print/${document.id}?press=true&api_key=${auth?.user?.access_token}`}
                            target={'_blank'}>Nyomdai verzió</a></Button>
                        <Button asChild variant={'outline'} className={"min-w-32"}><a
                            href={`/api/documents/${document.id}/download?api_key=${auth?.user?.access_token}`}
                            target={'_blank'}>Irodai letöltés</a></Button>
                        <Button asChild variant={'outline'} className={"min-w-32"}><a
                            href={`/api/documents/${document.id}/download?press=true&api_key=${auth?.user?.access_token}`}
                            target={'_blank'}>Nyomdai letöltés</a></Button>
                    </div>
                </div>
                <Separator/>
                <div className="container mx-auto my-4 px-4 grow">
                    <Card className={"w-full p-1"}>
                        <CardContent>
                            <div className={"w-full flex justify-between items-center"}>
                                <div className={"flex flex-col"}>
                                <span
                                    className={"font-black"}>{document.form.title ?? 'Vállalati Stratégia Tanulmány'}</span>
                                    <span
                                        className={"font-bold text-xs"}>{documentModules.map(module => (document.form.theme[module.id] ?? false) ? module.name : null).filter(item => item != null).join(', ')}</span>
                                    <span
                                        className={"font-bold text-xs text-muted-foreground mt-1"}>Létrehozva: {format(document.created_at, "yyyy.MM.dd.")}</span>
                                </div>
                                <div className={"inline-flex gap-1 items-center justify-center"}>
                                    <div
                                        className={"size-11 bg-sidebar rounded-md text-muted inline-flex items-center justify-center font-bold"}>{companyMonogram}
                                    </div>
                                    <div className={"inline-flex flex-col"}>

                                        <span className={"font-bold"}>{document.company?.name}</span>
                                        <span
                                            className={"font-bold text-muted-foreground text-sm"}>{document.company?.tax_number}</span>
                                    </div>
                                </div>
                                <div className={"inline-flex flex-col"}>
                                    <span className={"font-bold"}>{document.form?.contact?.name}</span>
                                    <span
                                        className={"text-muted-foreground text-sm font-bold"}>{document.form?.contact?.email}</span>
                                </div>
                                <div>
                                    <Button variant={'ghost'}
                                            className={"text-muted-foreground font-bold"}
                                            onClick={() => navigate(`/documents/${document.id}/edit`)}><PencilIcon/> Szerkesztés</Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <div className={"grid grid-cols-2 mt-8 gap-8"}>


                        <div className={"flex flex-col"}>
                            <Form formValue={confirmForm} onChange={setConfirmForm} onSubmit={submitConfirm}
                                  initialValues={confirmInitialValues}>
                                <span className={"text-xl font-black"}>Megrendelés megerősítő email küldése</span>
                                <div className={"flex gap-3"}>
                                    <TextField name={'email'} label={'Email'}></TextField>
                                    <div className="grid w-full max-w-sm items-center gap-1.5">
                                        <TextField name={'name'} label={'Név'}></TextField>
                                    </div>
                                </div>
                                <div className={"flex gap-3 items-center"}>
                                    <div className="grid w-full max-w-sm items-center gap-1.5">
                                        <CurrencyField name={"price"} label={"Tanácsadás díja"}/>
                                    </div>

                                    <div className={"w-full flex justify-end gap-2"}>
                                        {document.order?.id ? <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button variant={'outline'} onClick={() => {
                                                    setClipboard(`${window.location.origin}/api/order-confirm/${document.order?.url}`).then(()=>toast.success("Link vágólapra másolva."))
                                                }}><ClipboardCopyIcon/></Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Megrendelés link másolása</p>
                                            </TooltipContent>
                                        </Tooltip> :null}

                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button variant={'outline'} onClick={handleOrderReset}><ReloadIcon/></Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Megrendelés visszaállítása</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </div>


                                </div>
                                <div className={"grow"}></div>
                               <FormActions>
                                    {(actions) => <>
                                        <Button className={"w-full inline-flex gap-2"}
                                                onClick={actions.submit}
                                                disabled={document.order != null || confirmLoading}><SendIcon/>Küldés</Button>
                                    </>
                                    }
                                </FormActions>
                            </Form>
                        </div>
                        <div className={"flex flex-col"}>
                            <Form formValue={recipientsForm} onChange={setRecipientsForm} onSubmit={submitAccess}>
                                <span className={"text-xl font-black"}>Elkészült dokumentum kiküldése</span>
                                <div className={"flex flex-col gap-3"}>
                                    <FieldArray name={'recipients'} end={(actions) => (
                                        <Button disabled={!document.order?.confirmed} variant={'secondary'}
                                                onClick={() => actions.add({})}
                                                className={"w-full bg-sidebar text-muted hover:bg-neutral-900 inline-flex gap-2"}><PlusIcon/>Címzett
                                            hozzáadása</Button>
                                    )}>
                                        {(array) =>
                                            array.map((actions, index) => <>
                                                <div className="flex gap-1.5 items-end">
                                                    <TextField name={'email'} label={'Email'} validate={(v: string)=> EmailValidator(v, "Email")}></TextField>


                                                    <TextField name={'name'} label={'Név'} validate={(v: string) => RequiredValidator(v, "Név")}></TextField>

                                                    <Button onClick={() => actions.remove(index)} variant={"outline"}
                                                            className={"mb-4"}><X/></Button>
                                                </div>
                                            </>)
                                        }
                                    </FieldArray>

                                </div>

                                <div className={"grow mb-3"}></div>

                                <FormActions>
                                    {(actions) =>
                                        <Button className={"w-full inline-flex gap-2"}
                                                disabled={recipientsForm.fieldValues["recipients"]?.length == 0 || !hasError(recipientsForm.touchedFields) || hasError(recipientsForm.errors) || recipientsLoading}
                                                onClick={actions.submit}><SendIcon/>Küldés</Button>
                                    }
                                </FormActions>

                            </Form>
                        </div>
                    </div>
                </div>
            </div>
            }
        </>
    );
}

export default Overview;
